import { ReactNode, useEffect, useState } from "react"
import { useEventListener } from "./hooks/useEventListener.ts"

const π = Math.PI
const half_π = π * 0.5
const twice_π = 2.0 * π

function euclideanRemainder(x: number, y: number): number {
  return x - y * Math.floor(x / y)
}

function trimmedAltitude(altitude: number): number {
  return Math.max(-half_π, Math.min(altitude, half_π))
}

function trimmedAzimuth(azimuth: number): number {
  return euclideanRemainder(azimuth, twice_π)
}

export function MouseHandler(props: {
  element: HTMLElement,
  setAzimuth: React.Dispatch<React.SetStateAction<number>>,
  setAltitude: React.Dispatch<React.SetStateAction<number>>,
  setCameraDistance: React.Dispatch<React.SetStateAction<number>>,
}): ReactNode {
  const { element, setAzimuth, setAltitude, setCameraDistance } = props

  const [isDragging, setDragging] = useState(false)

  useEventListener('pointerdown', element, event => {
    if (event.button !== 0) {
      return
    }
    setDragging(true)

  })

  useEventListener('pointerup', element, event => {
    event.preventDefault()
    if (event.button !== 0) {
      return
    }
    setDragging(false)
  })

  useEventListener('mousemove', isDragging ? element : null, event => {
    const { movementX, movementY } = event

    const radiansPerPoint = 0.0005
    const dAzimuth = -movementX * radiansPerPoint
    const dAltitude = -movementY * radiansPerPoint
    setAzimuth(azimuth => trimmedAzimuth(azimuth + dAzimuth))
    setAltitude(altitude => trimmedAltitude(altitude + dAltitude))
  })

  usePointerLock(isDragging, element)

  useEventListener('wheel', element, event => {
    event.preventDefault()
    const dyMax = 8
    const dy = Math.max(-dyMax, Math.min(event.deltaY, dyMax))

    setCameraDistance(distance => distance * Math.exp(dy * 0.01))
  })

  return null
}

function usePointerLock(isDragging: boolean, element: HTMLElement) {
  useEffect(() => {
    if (!isDragging) {
      document.exitPointerLock()
      return
    }

    element.requestPointerLock({
      unadjustedMovement: true
    })
    return () => {
      document.exitPointerLock()
    }
  }, [isDragging, element])
}
